import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import {
  DataStateChangeEvent,
  GridDataResult,
} from '@progress/kendo-angular-grid'
import { State } from '@progress/kendo-data-query'
import { filter, map, Observable, skip } from 'rxjs'

import {
  Enums,
  Environment,
  ExpandedProfile,
  Hunt,
  Profile,
} from '@cyborg-monorepo/hunter/common'
import {
  DialogRef,
  DialogService,
  ProgressSegment,
  SnackbarService,
} from '@hyperkit'

import { HuntAPIService, SessionService } from '@hunt/services'

import { DateRangeFilterComponent } from '../../../shared/components/custom-filters/date-range-filter.component'
import { DropDownFilterComponent } from '../../../shared/components/custom-filters/dropdown-filter.component'
import { MultiselectEnvironmentFilterComponent } from '../../../shared/components/custom-filters/multiselect-environment-filter.component'
import { MultiselectTagFilterComponent } from '../../../shared/components/custom-filters/multiselect-tag-filter.component'
import { CompanyService } from '../../../shared/services/company.service'
import { EnvironmentService } from '../../../shared/services/environment.service'
import { ArchiveHuntDialogComponent } from '../../dialogs/archive-hunt-dialog/archive-hunt-dialog.component'
import {
  buildQueryProgressSegments,
  countQueriesInPackage,
  totalQueries,
} from '../../helpers/query-status-helper'

import { HuntStateService } from './hunt-state-service'
import { HuntUrlService } from './hunt-url-service'

@Component({
  standalone: false,
  templateUrl: './hunt-list.component.html',
  styleUrls: ['hunt-list.component.scss'],
})
export class HuntListComponent implements OnInit, AfterViewInit {
  router = inject(Router)
  private dialogService = inject(DialogService)
  private companyService = inject(CompanyService)
  huntUrlService = inject(HuntUrlService)
  huntStateService = inject(HuntStateService)
  private activatedRoute = inject(ActivatedRoute)
  private huntService = inject(HuntAPIService)
  private environmentService = inject(EnvironmentService)

  readonly session = inject(SessionService)
  public readonly snack = inject(SnackbarService)

  @ViewChildren('hunt_dropdown')
  public dropdowns: QueryList<DropDownFilterComponent>
  @ViewChildren('hunt_multiselect')
  public multiselects: QueryList<MultiselectTagFilterComponent>
  @ViewChildren('hunt_env_multiselect')
  public envMultiselects: QueryList<MultiselectEnvironmentFilterComponent>
  @ViewChild('hunt_datepicker') public datepicker: DateRangeFilterComponent

  public huntView: Hunt[]
  public hunts: Array<Hunt>
  public priority = Enums.PriorityEnum
  public status = Enums.StatusEnum
  public outcome = Enums.OutcomeEnum
  public huntData: GridDataResult = { data: [], total: 0 }
  public users$: Observable<Array<Profile & ExpandedProfile>>
  public items = [{ text: 'Metrics', icon: 'graph', selected: true }]
  public environments: Environment[]

  public statusFilters: Array<{
    text: string
    value: Enums.StatusEnum
    class: string
  }> = [
    { text: 'To Do', value: Enums.StatusEnum.TO_DO, class: 'k-color-info' },
    {
      text: 'In Progress',
      value: Enums.StatusEnum.IN_PROGRESS,
      class: 'k-color-warning',
    },
    { text: 'Done', value: Enums.StatusEnum.DONE, class: 'k-color-success' },
  ]

  public priorityFilters: Array<{
    text: string
    value: Enums.PriorityEnum
    class: string
  }> = [
    { text: 'Low', value: Enums.PriorityEnum.LOW, class: 'k-color-success' },
    {
      text: 'Medium',
      value: Enums.PriorityEnum.MEDIUM,
      class: 'k-color-warning',
    },
    { text: 'High', value: Enums.PriorityEnum.HIGH, class: 'k-color-error' },
  ]

  public outcomeFilters: Array<{
    text: string
    value: Enums.OutcomeEnum
    class: string
  }> = [
    {
      text: 'Detection',
      value: Enums.OutcomeEnum.DETECTION,
      class: 'k-color-warning',
    },
    {
      text: 'Escalated',
      value: Enums.OutcomeEnum.ESCALATED,
      class: 'k-color-error',
    },
    {
      text: 'No Findings',
      value: Enums.OutcomeEnum.NO_FINDINGS,
      class: 'k-color-success',
    },
    {
      text: 'Visibility Gap',
      value: Enums.OutcomeEnum.VISIBILITY_GAP,
      class: 'k-color-warning',
    },
  ]

  public ngOnInit(): void {
    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments
    })

    this.users$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) => {
        return users.map((user: Profile) => {
          return {
            ...user,
            ...{ fullName: user.firstName + ' ' + user.lastName },
          }
        })
      }),
    )

    this.huntStateService.huntState.pipe(skip(1)).subscribe((state: State) => {
      this.huntUrlService.gotoUrl(state)
    })
  }

  public ngAfterViewInit(): void {
    if (Object.keys(this.activatedRoute.snapshot.queryParams).length) {
      this.populateFromUrl(this.activatedRoute.snapshot.queryParams)
    } else {
      this.huntUrlService.gotoUrl(this.huntStateService.huntState.value)
    }
  }

  public archiveHunt(hunt: Hunt): void {
    const dialogRef: DialogRef = this.dialogService.open(
      ArchiveHuntDialogComponent,
      { data: hunt },
    )

    dialogRef
      .afterClosed()
      .pipe(filter((hunt) => hunt != null))
      .subscribe((hunt: Hunt) => {
        this.session.markDirty()
        this.huntStateService.updateState()
        this.snack.show(`Archived ${hunt.name}`, 5000)
      })
  }

  public unarchiveHunt(hunt: Hunt): void {
    this.huntService.unarchiveHunt(hunt.id).subscribe(async (hunt: Hunt) => {
      this.session.markDirty()
      this.huntStateService.updateState()
      this.snack.show(`Removed ${hunt.name} from archive`, 5000)
    })
  }

  public toggleArchivedHunts(): void {
    this.huntStateService.updateState({
      archived: !this.huntStateService.huntState.value.archived,
    })
  }

  private populateFromUrl(queryParams: Params): void {
    let searchState: State =
      this.huntUrlService.searchStateFromString(queryParams)
    if (!searchState) {
      searchState = this.huntStateService.huntState.value
    }
    this.huntStateService.setState(searchState)
  }

  public onHuntPageChange(e: DataStateChangeEvent): void {
    if (!e.sort[0].dir) e.sort = [{ field: 'name', dir: 'asc' }]

    this.huntStateService.updateState(e)
  }

  public clearAllFilters(): void {
    this.dropdowns.forEach((DropDownFilterComponent) => {
      DropDownFilterComponent.resetDefault()
    })

    this.multiselects.forEach((MultiselectTagFilterComponent) => {
      MultiselectTagFilterComponent.resetDefault()
    })

    this.datepicker.resetDefault()
    this.huntStateService.reset()
  }

  public getRouterLink(dataItem: any): string[] {
    return ['/hunt', dataItem.id]
  }

  /**
   * Get query status counts for a specific hunt
   */
  getQueryStatusCounts(hunt: Hunt): {
    toDo: number
    run: number
    skip: number
  } {
    if (!hunt.huntPackages?.length) {
      return { toDo: 0, run: 0, skip: 0 }
    }

    return hunt.huntPackages.reduce(
      (acc, pkg) => {
        const counts = countQueriesInPackage(pkg)
        acc.toDo += counts.toDo
        acc.run += counts.run
        acc.skip += counts.skip
        return acc
      },
      { toDo: 0, run: 0, skip: 0 },
    )
  }

  /**
   * Get progress segments for a hunt
   */
  getProgressSegments(hunt: Hunt): ProgressSegment[] {
    const counts = this.getQueryStatusCounts(hunt)
    return buildQueryProgressSegments(counts)
  }

  /**
   * Get total queries for a hunt
   */
  getTotalQueries(hunt: Hunt): number {
    const counts = this.getQueryStatusCounts(hunt)
    return totalQueries(counts)
  }
}
